import {produce} from 'immer';
import {useMutation, type FetchResult} from '@apollo/client';

import {updateApolloCache} from 'web-app/util/graphql';
import type {
    EnableBillPayerAutomaticInvoiceSendingMutation,
    EnableBillPayerAutomaticInvoiceSendingMutationVariables,
    UpdateBillingFrequenciesMutation,
    UpdateBillingFrequenciesMutationVariables,
} from 'web-app/react/profile/bill-payer-v2/__generated__/queries.api-types';
import {GetBillPayerAutomaticInvoicingConfig} from 'web-app/react/profile/bill-payer-v2/bill-payer-profile/queries';
import {
    EnableBillPayerAutomaticInvoiceSending,
    UpdateBillingFrequencies,
} from 'web-app/react/profile/bill-payer-v2/queries';
import type {GetBillPayerAutomaticInvoicingConfigQuery} from 'web-app/react/profile/bill-payer-v2/bill-payer-profile/__generated__/queries.api-types';

import type {AutomaticInvoicingData} from './use-submission-data';

type UseUpdateBillingFrequency = () => {
    updateBillingFrequency: (config: AutomaticInvoicingData) => Promise<FetchResult<UpdateBillingFrequenciesMutation>>;
};

export const useUpdateBillingFrequency: UseUpdateBillingFrequency = () => {
    const [setBillingFrequency] = useMutation<
        UpdateBillingFrequenciesMutation,
        UpdateBillingFrequenciesMutationVariables
    >(UpdateBillingFrequencies);

    const [enableBillPayerAutomaticInvoiceSending] = useMutation<
        EnableBillPayerAutomaticInvoiceSendingMutation,
        EnableBillPayerAutomaticInvoiceSendingMutationVariables
    >(EnableBillPayerAutomaticInvoiceSending, {});

    const updateBillingFrequency = (config: AutomaticInvoicingData) => {
        const {billPayerIds, invoicingFrequency, siteSetId, shouldEnableAutomaticInvoicing} = config;

        return setBillingFrequency({
            variables: {
                billPayerIds,
                invoicingFrequency,
            },
            onCompleted: () => {
                if (shouldEnableAutomaticInvoicing) {
                    enableBillPayerAutomaticInvoiceSending({
                        variables: {
                            billPayerIds,
                        },
                        update: updateApolloCache<GetBillPayerAutomaticInvoicingConfigQuery, {}>()({
                            type: 'query',
                            node: GetBillPayerAutomaticInvoicingConfig,
                            cacheVariables: {
                                siteSetId,
                                billPayerIds,
                            },
                            updater: (cache, result) => {
                                if (result.finance.invoices.automaticInvoicing.enableBillPayerAutomaticInvoiceSending) {
                                    return produce(cache, draft => {
                                        draft.finance.invoices.automaticInvoicing.billPayerAutomaticInvoicingConfig.forEach(
                                            config => {
                                                config.isEnabled = true;
                                            },
                                        );
                                    });
                                }

                                return cache;
                            },
                        }),
                    });
                }
            },
        });
    };

    return {updateBillingFrequency};
};
